import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function ContemporaryFinance53() {
  const contentRef = useRef(null);
  const titleText = "Creation and Destruction of Fiat Money";
  const links = {
    prev: "/contemporaryfinance-52",
    index: "/index-finance",
    next: "/contemporaryfinance-54",
  };
  const questions = [
    {
      text: "1. How is most fiat money in circulation created?",
      options: [
        {
          value: "A",
          label: "A. By governments printing physical notes",
        },
        {
          value: "B",
          label: "B. By banks issuing loans through debt creation",
        },
        {
          value: "C",
          label: "C. By citizens saving more",
        },
        {
          value: "D",
          label: "D. By international trade agreements",
        },
      ],
      correct: "B",
    },
    {
      text: "2. What happens to fiat money when a debt is repaid?",
      options: [
        { value: "A", label: "A. It continues to circulate permanently" },
        { value: "B", label: "B. It gets transferred to government reserves" },
        { value: "C", label: "C. It is destroyed, reducing the money supply" },
        {
          value: "D",
          label: "D. It is automatically reinvested in the economy",
        },
      ],
      correct: "C",
    },
    {
      text: "3. Why is the fiat money system considered unstable?",
      options: [
        {
          value: "A",
          label: "A. Because it is backed by scarce natural resources",
        },
        {
          value: "B",
          label: "B. Because its supply depends on constant debt expansion",
        },
        { value: "C", label: "C. Because governments restrict borrowing" },
        { value: "D", label: "D. Because it eliminates inflation" },
      ],
      correct: "B",
    },
    {
      text: "4. What is the greatest risk to the survival of fiat money?",
      options: [
        { value: "A", label: "A. Lack of new technology" },
        { value: "B", label: "B. Decline in international trade" },
        { value: "C", label: "C. Loss of public confidence in its value" },
        { value: "D", label: "D. Excessive savings by households" },
      ],
      correct: "C",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={53}
        timerMinutes={60}
        currentLevel={4}
        unlockedBadges={7}
      />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 53</b>! Today, we explore how fiat money is born, how
        it expands, and how it ultimately disappears. Unlike commodity money,
        which is tied to something tangible, fiat money comes into existence
        through the mechanisms of government borrowing and bank lending. At the
        same time, it vanishes when debts are repaid or when loans are written
        off. This cycle of creation and destruction makes fiat money a system of
        constant flux, where the supply of money is never fixed but always
        shifting. By understanding these processes, we begin to see how fragile
        the foundation of modern finance is, and why crises often emerge when
        confidence in the system weakens.
        <br />
        <br />
        <b>Creation of Fiat Money Through Debt</b>
        <br />
        <br />
        Fiat money, unlike commodity money backed by gold or silver, has no
        intrinsic cost of production beyond the administrative expenses of
        issuing currency and maintaining the financial system. Yet, even though
        its creation does not require the mining of resources or the
        labor-intensive extraction of precious metals, it is not without a
        system of birth and demise. Fiat money comes into existence primarily
        when new debt is incurred and leaves existence when that debt is either
        paid back or defaulted upon. This principle lies at the heart of the
        fiat monetary architecture. The money supply is therefore not a static
        pool of notes and coins but rather a constantly changing and elastic
        system that expands and contracts in response to credit activity.
        Economists classify this money supply into two broad categories: base
        money and broad money. Base money is composed of the physical currency
        circulating among households, businesses, and financial institutions,
        along with the reserves that commercial banks keep at the central bank.
        Because it is the direct liability of the central bank, base money
        represents the most foundational form of fiat currency in the system.
        When people hold banknotes in their hands, or when commercial banks keep
        balances with the central bank, what they are dealing with is base
        money. It is the responsibility of the central bank to determine how
        much of this base money exists at any given time, issuing new physical
        currency when the economy demands it or when old and damaged notes must
        be withdrawn and destroyed. The process is not arbitrary; the central
        bank must balance the economy’s needs for liquidity with the dangers of
        creating too much money that could trigger inflation.
        <br />
        <br />
        <b>Broad Money and the Fragility of Banking</b>
        <br />
        <br />
        While base money is important, it represents only a small fraction of
        the total monetary ecosystem. Far larger is broad money, which
        encompasses base money but also includes the deposits that people and
        businesses place with commercial banks. Broad money is essentially the
        circulating money supply as experienced in everyday life, because it
        covers savings accounts, checking accounts, term deposits, and other
        forms of bank liabilities that households and corporations use to make
        payments and settle debts. This makes broad money far more significant
        in quantity than base money, even though its foundation ultimately rests
        on base money itself. Broad money carries additional risks because it is
        partially dependent on the solvency of commercial banks. If a bank fails
        and depositor funds are not protected, then broad money is destroyed
        when those deposits vanish. The fragile nature of broad money highlights
        the fractional reserve system that governs modern finance: banks do not
        keep full reserves for all deposits but instead lend out a large portion
        of what they receive, creating new money through the extension of
        credit. In this system, central banks manage the relatively smaller pool
        of base money, while the commercial banking sector is responsible for
        the vast expanse of broad money. This arrangement makes the modern
        monetary system highly leveraged, with each unit of base money
        supporting multiple units of broad money. It is this layered and fragile
        relationship that makes crises in banking such as the 2008 financial
        collapse so impactful, because they directly threaten the stability of
        broad money and therefore the confidence of society in its money supply.
      </p>
      <img src="/base_broad_money.png" width="500"></img>
      <p className="content">
        <br />
        <br />
        <b>Forces That Shape the Money Supply</b>
        <br />
        <br />
        Even though central banks exercise significant authority over the supply
        of base money, the creation and destruction of broad money often lies
        beyond their immediate control. Government fiscal policies, budget
        deficits, and commercial bank lending practices all exert enormous
        influence on the size of broad money. For example, when governments
        issue treasuries to finance deficits, the buyers of those securities
        transfer their deposits into the government’s account at the central
        bank, shifting reserves within the system. Likewise, when banks extend
        loans, they create new deposits out of thin air. A borrower receiving a
        loan sees a balance appear in their account, yet no actual transfer of
        pre-existing money has occurred. Instead, the bank has created a
        liability to the borrower and an asset in the form of the loan. The
        commercial bank then adjusts its reserves at the central bank to balance
        these flows, creating an intricate web of transactions in which deposits
        constantly move across institutions. The opposite happens when loans are
        repaid or defaulted on, causing deposits to shrink and broad money to
        contract. Similarly, banks can alter their cash reserves by buying or
        selling assets. When a bank sells assets, it increases its reserves but
        drains deposits from other parts of the system as those deposits move to
        pay for the transaction. The ratio of broad money to base money is
        therefore a fluid figure that reflects credit expansion, government
        deficits, and consumer behavior, rather than a fixed ratio set in stone.
        Individuals withdrawing cash from their bank accounts can also change
        this ratio by converting deposits into physical currency, thereby
        increasing the portion of base money circulating outside the banks. This
        dynamic balance explains why central banks often find it difficult to
        directly control broad money and why monetary policy relies heavily on
        influencing interest rates, credit conditions, and market behavior
        rather than commanding the exact size of the money supply.
        <br />
        <br />
        <b>Central Bank Tools and the Cycle of Money</b>
        <br />
        <br />
        The tools available to central banks for directly altering the base
        money supply are most visibly represented by the practices known as
        quantitative easing and quantitative tightening. Quantitative easing,
        often abbreviated as QE, occurs when the central bank creates new
        reserves electronically and uses them to purchase existing financial
        assets such as government treasuries or mortgage-backed securities. By
        doing so, the central bank injects new base money into the system,
        increasing bank reserves and lowering interest rates in order to
        stimulate lending and investment. The process does not create new
        physical currency but expands the electronic reserves that form the
        backbone of modern settlement systems. In contrast, quantitative
        tightening or QT is the reversal of this process. Here the central bank
        sells the assets it holds, such as treasuries or mortgage-backed
        securities, and in exchange absorbs reserves from the banking system.
        Those reserves are effectively deleted, shrinking the supply of base
        money and tightening financial conditions. Together, QE and QT form the
        modern central bank’s most powerful levers for managing the size of base
        money, and through it, indirectly, the scale of broad money. These
        policies illustrate the unique nature of fiat currency, which can be
        created and destroyed not through physical mining or the discovery of
        resources but through deliberate accounting decisions at the heart of
        the central banking system. However, while such measures provide
        flexibility and allow governments to respond to crises, they also carry
        risks of mismanagement, inflation, or asset bubbles when used
        excessively. The cycle of money creation and destruction therefore
        remains both the strength and the vulnerability of the fiat system,
        reminding us that the trust and stability of modern economies depend not
        only on the mechanics of money but also on the discipline of those who
        govern it.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        The lesson explained that fiat money has no independent value and exists
        only because it is issued by governments and banks through debt. New
        money is created whenever loans are extended or governments borrow,
        while money disappears when debts are repaid, written off, or defaulted.
        This constant cycle of expansion and contraction means the money supply
        is never stable, leaving economies vulnerable to inflation, deflation,
        and financial crises. The system thrives on perpetual debt, and its
        survival depends on maintaining public confidence.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
        Up next, we will continue our exploration of the distortions caused by
        fiat money by looking at how this system alters the very fabric of human
        civilization. Day 54 will focus on the ways fiat money reshapes
        incentives, pushing societies away from productivity and long-term
        stability and towards dependency, short-term consumption, and fragile
        foundations. We will examine how the rise of fiat has changed human time
        preference, leaving people more concerned with immediate gratification
        rather than building for the future, and how this shift is reflected in
        architecture, environmental exploitation, reckless borrowing, and
        strained family structures. By the end of the lesson, it will become
        clear that fiat money is not only an economic system but also a cultural
        force that influences the survival, values, and direction of entire
        civilizations.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default ContemporaryFinance53;
